// src/components/CartDrawer.js
import { useState } from 'react';
import { FiX, FiFileText, FiCheck, FiAlertCircle } from 'react-icons/fi';
import ContractModal from './ContractModal';

function CartDrawer({ selectedPackage, selectedAddons, packages, addons, onClose, onSubmitSuccess }) {
  const [contractOpen, setContractOpen] = useState(false);
  const [contractRead, setContractRead] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | sending | sent | error
  const [contractError, setContractError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ name: false, email: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const pkg = packages.find(p => p.id === selectedPackage);
  const addonItems = addons.filter(a => selectedAddons.has(a.id));
  const hasItems = pkg || addonItems.length > 0;

  const total = (pkg?.price ?? 0) + addonItems.reduce((sum, a) => sum + a.price, 0);

  async function handleSubmit() {
    const errors = { name: !name.trim(), email: !email.trim() };
    setFieldErrors(errors);
    if (errors.name || errors.email) return;
    if (!contractRead) {
      setContractError(true);
      return;
    }
    setContractError(false);
    setSubmitStatus('sending');
    const addonLines = addonItems.map(a => `${a.label} — $${a.price.toLocaleString()}`).join(', ');
    const payload = {
      _subject: `Website Enquiry — ${pkg ? pkg.name : 'Add-ons Only'}`,
      name,
      email,
      package: pkg ? `${pkg.tier} — ${pkg.name} (${pkg.priceDisplay ?? `$${pkg.price.toLocaleString()}`})` : 'None',
      addons: addonLines || 'None',
      estimated_total: `$${total.toLocaleString()}`,
      note: 'Client has read and acknowledged the CS Devs Services Agreement.',
    };
    try {
      const res = await fetch('https://formspree.io/f/xyknynvq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitStatus('sent');
        onSubmitSuccess?.();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  }

  return (
    <>
      <div className="cart-overlay" onClick={onClose} />

      <aside className="cart-drawer">
        {/* Header */}
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">Your Selection</h2>
          <button className="cart-close-btn" onClick={onClose} aria-label="Close cart">
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="cart-drawer-body">
          {!hasItems ? (
            <p className="cart-empty">No items selected yet. Choose a package or add-ons to get started.</p>

          ) : submitStatus === 'sent' ? (
            <div className="cart-success">
              <FiCheck size={40} className="cart-success-icon" />
              <h3>Enquiry Submitted!</h3>
              <p>
                We've received your selection and will be in touch within <strong>48 hours</strong>.
              </p>
            </div>

          ) : (
            <>
              {/* Line items */}
              <ul className="cart-items">
                {pkg && (
                  <li className="cart-item cart-item--package">
                    <div className="cart-item-info">
                      <span className="cart-item-tier">{pkg.tier}</span>
                      <span className="cart-item-name">{pkg.name}</span>
                    </div>
                    <span className="cart-item-price">{pkg.priceDisplay ?? `$${pkg.price.toLocaleString()}`}</span>
                  </li>
                )}
                {addonItems.length > 0 && (
                  <li className="cart-addons-label">Add-ons</li>
                )}
                {addonItems.map(a => (
                  <li key={a.id} className="cart-item cart-item--addon">
                    <span className="cart-item-name">{a.label}</span>
                    <span className="cart-item-price">${a.price.toLocaleString()}</span>
                  </li>
                ))}
              </ul>

              {/* Total */}
              <div className="cart-total-row">
                <span className="cart-total-label">Estimated Total</span>
                <span className="cart-total-amount">${total.toLocaleString()}</span>
              </div>

              {/* Contact details */}
              <div className="cart-contact-section">
                <div className="cart-contact-field">
                  <label htmlFor="cart-name" className="cart-contact-label">
                    Your Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    id="cart-name"
                    type="text"
                    className={`cart-contact-input${fieldErrors.name ? ' cart-contact-input--error' : ''}`}
                    placeholder="Jane Smith"
                    value={name}
                    onChange={e => { setName(e.target.value); setFieldErrors(prev => ({ ...prev, name: false })); }}
                  />
                  {fieldErrors.name && <p className="cart-field-error">Please enter your name.</p>}
                </div>
                <div className="cart-contact-field">
                  <label htmlFor="cart-email" className="cart-contact-label">
                    Email Address <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    id="cart-email"
                    type="email"
                    className={`cart-contact-input${fieldErrors.email ? ' cart-contact-input--error' : ''}`}
                    placeholder="jane@example.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: false })); }}
                  />
                  {fieldErrors.email && <p className="cart-field-error">Please enter your email address.</p>}
                </div>
              </div>

              {/* Contract */}
              <div className="cart-contract-section">
                <button className="cart-contract-btn" onClick={() => setContractOpen(true)}>
                  <FiFileText size={14} />
                  View the CS Devs Contract
                </button>
                {contractRead && (
                  <span className="cart-contract-confirmed">
                    <FiCheck size={13} /> Contract read &amp; acknowledged
                  </span>
                )}
              </div>

              {/* Disclaimer */}
              <div className="cart-disclaimer">
                <FiAlertCircle size={14} className="cart-disclaimer-icon" />
                <p>
                  <strong>No payment will be taken.</strong> Submitting this enquiry sends an email
                  to CS Devs with your selections. We will review your request and respond within{' '}
                  <strong>48 hours</strong> to discuss next steps and confirm pricing.
                </p>
              </div>

              {/* Submit */}
              <button
                className="btn-cta cart-submit-btn"
                disabled={submitStatus === 'sending'}
                onClick={handleSubmit}
              >
                {submitStatus === 'sending' ? 'Submitting…' : 'Submit Enquiry'}
              </button>
              {contractError && (
                <p className="cart-contract-required">
                  You must read and acknowledge the CS Devs contract before submitting.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="cart-contract-required">
                  Something went wrong. Please try again or email us directly at owner@csdevs.com.
                </p>
              )}
            </>
          )}
        </div>
      </aside>

      {contractOpen && (
        <ContractModal
          onClose={() => setContractOpen(false)}
          onAccept={() => setContractRead(true)}
        />
      )}
    </>
  );
}

export default CartDrawer;
